// src/scenes/contributions/ContributionSchedule.jsx
import React, { useMemo, useState } from "react";
import { Box, useTheme, Button, Snackbar, Alert, Stack, Chip, alpha, useMediaQuery } from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import Header from "components/Header";
import { gql, useQuery, useMutation } from "@apollo/client";
import { useParams, useLocation } from "react-router-dom";
import { jwtDecode } from "jwt-decode";
import AddIcon from "@mui/icons-material/Add";

import ShareForm from "./ShareForm";

// =====================
// GraphQL
// =====================
const GET_CONTRIBUTION_SCHEDULE = gql`
  query GetSchedule($userId: Int!) {
    contributionScheduleByUser(userId: $userId) {
      id
      month
      scheduleYear
      dueDate
      expectedAmount
      paid
      createdDate
    }
  }
`;

const GET_SHARES_BY_USER = gql`
  query SharesByUser($userId: Int, $year: String) {
    memberContributionSharesByUser(userId: $userId, year: $year) {
      id
      shares
      year
      userId
      groupId
      createdDate
      updatedDate
      createdBy
      updatedBy
    }
  }
`;

const CREATE_SHARE = gql`
  mutation CreateShare($newShare: MemberContributionSharesInput!) {
    createMemberContributionShare(newShare: $newShare) {
      id
      shares
      year
      userId
      groupId
      createdDate
      updatedDate
      createdBy
      updatedBy
    }
  }
`;

// =====================
// Helpers
// =====================
const getUserIdFromToken = () => {
  const token = localStorage.getItem("token");
  if (!token) return null;

  try {
    const decoded = jwtDecode(token);
    return parseInt(decoded.Id);
  } catch (err) {
    console.error("Invalid token", err);
    return null;
  }
};

const currentYear = String(new Date().getFullYear());

const ContributionSchedule = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const { userId: paramUserId } = useParams();
  const { state } = useLocation();
  const passedFirstName = state?.firstName;

  const tokenUserId = getUserIdFromToken();
  const userId = paramUserId ? parseInt(paramUserId) : tokenUserId;

  const isSelf = !paramUserId; // viewing my own schedule (route without userId)

  const [toast, setToast] = useState({ type: "success", msg: "" });
  const showToast = (type, msg) => setToast({ type, msg });

  // modal
  const [openShareForm, setOpenShareForm] = useState(false);

  // =====================
  // Schedule query
  // =====================
  const { data, loading, error, refetch: refetchSchedule } = useQuery(
    GET_CONTRIBUTION_SCHEDULE,
    {
      variables: { userId },
      skip: !userId,
    }
  );

  // =====================
  // Shares query (for the schedule owner, and current year by default)
  // =====================
  const {
    data: sharesData,
    loading: sharesLoading,
    refetch: refetchShares,
  } = useQuery(GET_SHARES_BY_USER, {
    variables: { userId, year: currentYear },
    skip: !userId,
    fetchPolicy: "cache-and-network",
  });

  const sharesRows = useMemo(
    () => sharesData?.memberContributionSharesByUser ?? [],
    [sharesData]
  );

  const latestShare = useMemo(() => {
    if (!sharesRows.length) return null;

    // latest by createdDate/updatedDate fallback
    const sorted = [...sharesRows].sort((a, b) => {
      const ad = a?.updatedDate || a?.createdDate;
      const bd = b?.updatedDate || b?.createdDate;
      return new Date(bd || 0).getTime() - new Date(ad || 0).getTime();
    });
    return sorted[0];
  }, [sharesRows]);

  // =====================
  // Create share mutation
  // =====================
  const [createShare, { loading: creatingShare }] = useMutation(CREATE_SHARE, {
    // if your shares mutations live on groupClient, add: client: groupClient
    // but you didn't show the client usage here. If needed, import groupClient and set it.
  });

  const handleOpenShareForm = () => setOpenShareForm(true);
  const handleCloseShareForm = () => setOpenShareForm(false);

  const handleSaveShare = async ({ shares, year }) => {
    try {
      const sharesNum = Number(shares);
      const y = String(year || "").trim();

      if (!sharesNum || sharesNum <= 0) {
        showToast("error", "Please enter a valid shares number.");
        return;
      }
      if (!y || y.length !== 4) {
        showToast("error", "Please enter a valid year e.g. 2026.");
        return;
      }

      const { data: res } = await createShare({
        variables: { newShare: { shares: sharesNum, year: y } },
      });

      const created = res?.createMemberContributionShare;
      if (!created?.id) {
        showToast("error", "Failed to save shares.");
        return;
      }

      await Promise.allSettled([refetchShares(), refetchSchedule()]);
      showToast("success", "Shares updated successfully.");
      handleCloseShareForm();
    } catch (e) {
      console.error(e);
      showToast("error", e?.message || "Error saving shares.");
    }
  };

  // =====================
  // Grid columns
  // =====================
  const rows = useMemo(
    () =>
      (data?.contributionScheduleByUser || []).map((row, index) => ({
        ...row,
        sequence: index + 1,
      })),
    [data]
  );

  const columns = [
    { field: "sequence", headerName: "#", width: 70, sortable: false, filterable: false },
    { field: "month", headerName: "Month", flex: 1 },
    { field: "scheduleYear", headerName: "Year", flex: 0.6 },
    {
      field: "dueDate",
      headerName: "Due Date",
      flex: 1,
      valueFormatter: ({ value }) => new Date(value).toLocaleDateString(),
    },
    {
      field: "expectedAmount",
      headerName: "Amount (Ksh)",
      flex: 1,
      valueFormatter: ({ value }) => (value != null ? value.toLocaleString() : "-"),
    },
    {
      field: "paid",
      headerName: "Paid",
      flex: 0.6,
      renderCell: ({ value }) => (
        <Chip
          label={value ? "Paid" : "Pending"}
          size="small"
          color={value ? "success" : "warning"}
          variant={value ? "filled" : "outlined"}
          sx={{ fontWeight: 700 }}
        />
      ),
    },
  ];

  if (!userId) return <p>User not authenticated.</p>;
  if (error) return <p>Error loading contribution schedule: {error.message}</p>;

  return (
    <Box sx={{ m: { xs: "0.75rem", sm: "1rem 1.5rem", md: "1.5rem 2.5rem" }, minWidth: 0 }}>
      <Header
        title={
          isSelf
            ? "MY CONTRIBUTION SCHEDULE"
            : `${passedFirstName ? `${passedFirstName.toUpperCase()}'s` : "USER"} CONTRIBUTION SCHEDULE`
        }
        subtitle={
          isSelf
            ? "Expected Monthly Contributions"
            : `Contribution history for ${passedFirstName || "user"}`
        }
      />

      {/* Shares summary + action */}
      <Box mt={2} mb={2}>
        <Stack direction={{ xs: "column", md: "row" }} spacing={1.5} alignItems="center" justifyContent="space-between">
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
            <Chip
              label={`Year: ${currentYear}`}
              variant="outlined"
              size="small"
            />

            {sharesLoading ? (
              <Chip label="Loading shares..." size="small" variant="outlined" />
            ) : latestShare ? (
              <Chip
                label={`My Shares: ${latestShare.shares}`}
                size="small"
                sx={{ fontWeight: 800 }}
              />
            ) : (
              <Chip
                label="No shares set for this year"
                size="small"
                color="warning"
                variant="outlined"
              />
            )}
          </Stack>

          {/* Only allow members to set their own shares (not when admin viewing other user) */}
          {isSelf ? (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleOpenShareForm}
              disabled={creatingShare}
              sx={{ borderRadius: 2, fontWeight: 800 }}
            >
              {latestShare ? "Update My Shares" : "Set My Shares"}
            </Button>
          ) : null}
        </Stack>
      </Box>

      {/* Share modal */}
      <ShareForm
        open={openShareForm}
        onClose={handleCloseShareForm}
        onSubmit={handleSaveShare}
        saving={creatingShare}
        defaultYear={currentYear}
        existingShares={latestShare?.shares ?? ""}
      />

      {/* Toast */}
      <Snackbar
        open={!!toast.msg}
        autoHideDuration={4500}
        onClose={() => setToast({ type: "success", msg: "" })}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={() => setToast({ type: "success", msg: "" })}
          severity={toast.type}
          sx={{ width: "100%" }}
        >
          {toast.msg}
        </Alert>
      </Snackbar>

      {/* Schedule grid */}
      <Box
        mt="20px"
        height="75vh"
        sx={{
          "& .MuiDataGrid-root": {
            border: `1px solid ${alpha(theme.palette.secondary[200], 0.18)}`,
            borderRadius: "12px",
            overflow: "hidden",
          },
          "& .MuiDataGrid-cell": { borderBottom: `1px solid ${alpha(theme.palette.secondary[200], 0.08)}` },
          "& .MuiDataGrid-columnHeaders": {
            backgroundColor: theme.palette.background.alt,
            color: theme.palette.secondary[100],
            borderBottom: `1px solid ${alpha(theme.palette.secondary[200], 0.12)}`,
          },
          "& .MuiDataGrid-virtualScroller": {
            backgroundColor: theme.palette.primary.light,
          },
          "& .MuiDataGrid-footerContainer": {
            backgroundColor: theme.palette.background.alt,
            color: theme.palette.secondary[100],
            borderTop: `1px solid ${alpha(theme.palette.secondary[200], 0.12)}`,
          },
        }}
      >
        <DataGrid
          loading={loading}
          rows={rows}
          getRowId={(row) => row.id}
          columns={columns}
          rowHeight={52}
        
          columnVisibilityModel={
            isMobile
              ? {
                year: false,
                dueDate: false,
              }
              : undefined
          }/>
      </Box>
    </Box>
  );
};

export default ContributionSchedule;
